import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  ApprovalActionRecord,
  ApprovalDelegation,
  ApprovalInstance,
  ApprovalMatrixRow,
  ApprovalWorkflow,
} from '@/types/approvals';

export const approvalsApi = {
  listWorkflows: (ownerModule?: string) =>
    unwrap(
      api.get<ApiResponse<ApprovalWorkflow[]>>('/v1/approvals/workflows', {
        params: ownerModule ? { ownerModule } : undefined,
      }),
    ),

  getWorkflow: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}`)),

  createWorkflow: (body: Partial<ApprovalWorkflow>) =>
    unwrap(api.post<ApiResponse<ApprovalWorkflow>>('/v1/approvals/workflows', body)),

  updateWorkflow: (id: number, body: Partial<ApprovalWorkflow>) =>
    unwrap(api.put<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}`, body)),

  cloneWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<ApprovalWorkflow>>(`/v1/approvals/workflows/${id}/clone`)),

  deactivateWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/workflows/${id}/deactivate`)),

  archiveWorkflow: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/workflows/${id}/archive`)),

  matrix: () => unwrap(api.get<ApiResponse<ApprovalMatrixRow[]>>('/v1/approvals/matrix')),

  inbox: (status?: string, ownerModule?: string, mineOnly?: boolean) =>
    unwrap(
      api.get<ApiResponse<ApprovalInstance[]>>('/v1/approvals/inbox', {
        params: { status, ownerModule, mineOnly: mineOnly ? true : undefined },
      }),
    ),

  getInstance: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalInstance>>(`/v1/approvals/instances/${id}`)),

  act: (id: number, action: string, comments?: string, delegateToUserId?: number) =>
    unwrap(
      api.post<ApiResponse<{ status: string }>>(`/v1/approvals/instances/${id}/act`, {
        action,
        comments,
        delegateToUserId,
      }),
    ),

  resubmit: (id: number) =>
    unwrap(api.post<ApiResponse<{ instanceId: number; status: string }>>(`/v1/approvals/instances/${id}/resubmit`)),

  timeline: (id: number) =>
    unwrap(api.get<ApiResponse<ApprovalActionRecord[]>>(`/v1/approvals/instances/${id}/timeline`)),

  listDelegations: () =>
    unwrap(api.get<ApiResponse<ApprovalDelegation[]>>('/v1/approvals/delegations')),

  createDelegation: (body: {
    delegateUserId: number;
    ownerModule?: string;
    reason?: string;
    effectiveFrom?: string;
    effectiveTo: string;
  }) => unwrap(api.post<ApiResponse<ApprovalDelegation>>('/v1/approvals/delegations', body)),

  deactivateDelegation: (id: number) =>
    unwrap(api.post<ApiResponse<{ status: string }>>(`/v1/approvals/delegations/${id}/deactivate`)),
};
